<template>
    <div class="floor-plan-component">
        <!-- Karten-Container -->
        <div
            ref="mapContainer"
            class="map-container"
            :style="{ height: viewportHeight + 'px' }"
            @mousedown="handlePointerDown"
            @mousemove="handlePointerMove"
            @mouseup="handlePointerUp"
            @mouseleave="handlePointerUp"
            @touchstart="handlePointerDown"
            @touchmove="handlePointerMove"
            @touchend="handleTouchEnd"
            @click="handleMapClick"
        >
            <!-- Zoom-Buttons -->
            <div class="zoom-controls">
                <button @click.stop="zoomIn" class="zoom-btn zoom-btn-plus">+</button>
                <button @click.stop="zoomOut" class="zoom-btn zoom-btn-minus">−</button>
            </div>
            <!-- Grundriss-Bild -->
            <img
                ref="floorPlanImage"
                :src="floorPlanSrc"
                class="floor-plan-image"
                :style="imageStyle"
                @load="onImageLoad"
                @dragstart.prevent
            />

            <!-- Pins -->
            <div
                v-for="pin in pins"
                :key="`pin-${pin.id}`"
                class="pin"
                :class="{ 'pin-active': selectedPinId === pin.id }"
                :style="getPinStyle(pin)"
                @click.stop="selectPin(pin.id)"
                @touchend.stop="selectPin(pin.id)"
            >
                <div class="pin-marker"></div>
            </div>
        </div>

        <div v-if="pinsCountExceeded" class="pin-limit-warning">
            Maximale Anzahl von {{ maxPins }} Pins erreicht.
        </div>

        <!-- Kommentar-Eingabebereich -->
        <div v-if="selectedPinId" class="comment-section">
            <div class="input-group">
                <input
                    ref="commentInput"
                    type="text"
                    v-model="currentComment"
                    placeholder="Kommentar eingeben..."
                    class="form-control"
                    maxlength="150"
                    @input="updateComment"
                    @keydown.enter="blurInput"
                />
                <button @click="deletePin" class="btn btn-danger" type="button">
                    <i class="fas fa-trash"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FloorPlanComponent',
    props: {
        value: {
            type: Array,
            default: () => []
        },
        floorPlanSrc: {
            type: String,
            required: true
        },
        maxPins: {
            type: Number,
            default: 3
        },
        viewportHeight: {
            type: Number,
            default: 400
        },
        zoomLevel: {
            type: Number,
            default: 1
        }
    },
    data() {
        return {
            pins: [],
            selectedPinId: null,
            currentComment: '',
            isDragging: false,
            hasDragged: false,
            dragStart: { x: 0, y: 0 },
            pointerDownPosition: { x: 0, y: 0 },
            imagePosition: { x: 0, y: 0 },
            imageSize: { width: 0, height: 0 },
            nextPinId: 1,
            currentZoomLevel: 1,
            isImageLoaded: false,
            // Pinch-to-Zoom Variablen
            isPinching: false,
            initialPinchDistance: 0,
            initialZoomLevel: 1,
            pinchCenter: { x: 0, y: 0 }
        };
    },
    computed: {
        pinsCountExceeded() {
            return this.pins.length >= this.maxPins;
        },
        imageStyle() {
            return {
                transform: `translate(${this.imagePosition.x}px, ${this.imagePosition.y}px) scale(${this.currentZoomLevel})`,
                transformOrigin: '0 0'
            };
        }
    },
    mounted() {
        this.currentZoomLevel = this.zoomLevel;
    },
    watch: {
        value: {
            handler(newValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(this.getOutputData())) {
                    this.loadPinsFromValue(newValue);
                }
            },
            immediate: true,
            deep: true
        },
        pins: {
            handler() {
                this.emitData();
            },
            deep: true
        }
    },
    methods: {
        // Universelle Pointer-Event-Handler für Maus und Touch
        getPointerPosition(event) {
            if (event.touches && event.touches.length > 0) {
                return {
                    x: event.touches[0].clientX,
                    y: event.touches[0].clientY
                };
            }
            return {
                x: event.clientX,
                y: event.clientY
            };
        },

        // Pinch-to-Zoom Funktionen
        getTouchDistance(touch1, touch2) {
            const dx = touch1.clientX - touch2.clientX;
            const dy = touch1.clientY - touch2.clientY;
            return Math.sqrt(dx * dx + dy * dy);
        },

        getTouchCenter(touch1, touch2) {
            return {
                x: (touch1.clientX + touch2.clientX) / 2,
                y: (touch1.clientY + touch2.clientY) / 2
            };
        },

        handlePointerDown(event) {
            // Pinch-to-Zoom für Touch-Geräte
            if (event.touches && event.touches.length === 2) {
                this.isPinching = true;
                this.isDragging = false;
                this.initialPinchDistance = this.getTouchDistance(event.touches[0], event.touches[1]);
                this.initialZoomLevel = this.currentZoomLevel;
                this.pinchCenter = this.getTouchCenter(event.touches[0], event.touches[1]);
                event.preventDefault();
                return;
            }

            if (event.target === this.$refs.mapContainer || event.target === this.$refs.floorPlanImage) {
                const pointer = this.getPointerPosition(event);
                this.isDragging = true;
                this.hasDragged = false;
                this.dragStart = {
                    x: pointer.x - this.imagePosition.x,
                    y: pointer.y - this.imagePosition.y
                };
                this.pointerDownPosition = {
                    x: pointer.x,
                    y: pointer.y
                };
                event.preventDefault();
            }
        },

        handlePointerMove(event) {
            // Pinch-to-Zoom verarbeiten
            if (this.isPinching && event.touches && event.touches.length === 2) {
                const currentDistance = this.getTouchDistance(event.touches[0], event.touches[1]);
                const scale = currentDistance / this.initialPinchDistance;
                const newZoom = Math.min(Math.max(this.initialZoomLevel * scale, 0.1), 3);

                if (newZoom !== this.currentZoomLevel) {
                    const oldZoom = this.currentZoomLevel;
                    this.currentZoomLevel = newZoom;

                    // Zoom um den Pinch-Mittelpunkt
                    const container = this.$refs.mapContainer;
                    const containerRect = container.getBoundingClientRect();
                    const centerX = this.pinchCenter.x - containerRect.left;
                    const centerY = this.pinchCenter.y - containerRect.top;

                    const imagePointX = (centerX - this.imagePosition.x) / oldZoom;
                    const imagePointY = (centerY - this.imagePosition.y) / oldZoom;

                    this.imagePosition = {
                        x: centerX - imagePointX * this.currentZoomLevel,
                        y: centerY - imagePointY * this.currentZoomLevel
                    };
                }

                event.preventDefault();
                return;
            }

            if (this.isDragging) {
                const pointer = this.getPointerPosition(event);
                const deltaX = Math.abs(pointer.x - this.pointerDownPosition.x);
                const deltaY = Math.abs(pointer.y - this.pointerDownPosition.y);

                // Nur als Drag werten wenn mehr als 5 Pixel bewegt wurde
                if (deltaX > 5 || deltaY > 5) {
                    this.hasDragged = true;
                }

                this.imagePosition = {
                    x: pointer.x - this.dragStart.x,
                    y: pointer.y - this.dragStart.y
                };
                event.preventDefault();
            }
        },

        handlePointerUp() {
            this.isDragging = false;
            this.isPinching = false;
            // hasDragged nach kurzer Zeit zurücksetzen
            setTimeout(() => {
                this.hasDragged = false;
            }, 10);
        },

        onImageLoad() {
            const img = this.$refs.floorPlanImage;
            this.imageSize = {
                width: img.naturalWidth,
                height: img.naturalHeight
            };
            this.isImageLoaded = true;

            // Automatischen Zoom berechnen wenn kein expliziter zoomLevel gesetzt wurde
            if (this.zoomLevel === 1) {
                this.calculateInitialZoom();
            }
        },

        calculateInitialZoom() {
            this.$nextTick(() => {
                const container = this.$refs.mapContainer;
                if (!container || !this.imageSize.width || !this.imageSize.height) return;

                const containerWidth = container.clientWidth;
                const containerHeight = container.clientHeight;

                const scaleX = containerWidth / this.imageSize.width;
                const scaleY = containerHeight / this.imageSize.height;

                // Kleineren Skalierungsfaktor wählen, damit das ganze Bild sichtbar ist
                this.currentZoomLevel = Math.min(scaleX, scaleY) * 0.9; // 90% für etwas Padding

                // Bild zentrieren
                this.centerImage();
            });
        },

        centerImage() {
            const container = this.$refs.mapContainer;
            if (!container) return;

            const scaledWidth = this.imageSize.width * this.currentZoomLevel;
            const scaledHeight = this.imageSize.height * this.currentZoomLevel;

            this.imagePosition = {
                x: (container.clientWidth - scaledWidth) / 2,
                y: (container.clientHeight - scaledHeight) / 2
            };
        },

        loadPinsFromValue(value) {
            if (Array.isArray(value)) {
                this.pins = value.map((item, index) => ({
                    id: index + 1,
                    x: item.x || 0,
                    y: item.y || 0,
                    comment: item.comment || ''
                }));
                this.nextPinId = this.pins.length + 1;
            } else {
                this.pins = [];
                this.nextPinId = 1;
            }
        },

        getOutputData() {
            return this.pins.map(pin => ({
                x: pin.x,
                y: pin.y,
                comment: pin.comment
            }));
        },

        // Debounced emit für bessere Performance
        emitData() {
            if (this.emitTimeout) {
                clearTimeout(this.emitTimeout);
            }
            this.emitTimeout = setTimeout(() => {
                this.$emit('input', this.getOutputData());
            }, 100);
        },

        handleTouchEnd(event) {
            // Zuerst das normale Pointer-Up-Verhalten
            this.handlePointerUp();

            // Dann Pin setzen wenn nicht gedraggt wurde und nicht gepincht wurde
            if (!this.hasDragged && !this.isPinching) {
                this.handleMapClick(event);
            }
        },

        handleMapClick(event) {
            // Nicht reagieren wenn gezogen wurde oder auf Zoom-Button geklickt wurde
            if (this.hasDragged || event.target.closest('.zoom-controls')) {
                return;
            }

            if (this.pins.length >= this.maxPins) {
                this.showMaxPinsWarning();
                return;
            }

            const rect = this.$refs.mapContainer.getBoundingClientRect();
            // Für Touch-Events clientX/Y direkt verwenden, für Maus-Events normal
            let clientX, clientY;
            if (event.type === 'touchend' && event.changedTouches && event.changedTouches.length > 0) {
                clientX = event.changedTouches[0].clientX;
                clientY = event.changedTouches[0].clientY;
            } else {
                clientX = event.clientX;
                clientY = event.clientY;
            }

            const x = (clientX - rect.left - this.imagePosition.x) / this.currentZoomLevel;
            const y = (clientY - rect.top - this.imagePosition.y) / this.currentZoomLevel;

            // Prüfen ob Klick auf dem Bild ist
            if (x >= 0 && y >= 0 && x <= this.imageSize.width && y <= this.imageSize.height) {
                this.addPin(x, y);
            }
        },

        showMaxPinsWarning() {
            // Moderne Alternative zu alert()
            if (window.confirm) {
                window.confirm(`Maximale Anzahl von ${this.maxPins} Pins erreicht!`);
            }
        },

        addPin(x, y) {
            if (this.pins.length >= this.maxPins) {
                return;
            }

            const newPin = {
                id: this.nextPinId++,
                x: Math.round(x),
                y: Math.round(y),
                comment: ''
            };

            this.pins.push(newPin);
            this.selectPin(newPin.id);
        },

        selectPin(pinId) {
            this.selectedPinId = pinId;
            const selectedPin = this.pins.find(p => p.id === pinId);
            this.currentComment = selectedPin ? selectedPin.comment : '';

            this.$nextTick(() => {
                if (this.$refs.commentInput) {
                    this.$refs.commentInput.focus();
                }
            });
        },

        getPinStyle(pin) {
            return {
                left: (pin.x * this.currentZoomLevel + this.imagePosition.x - 15) + 'px',
                top: (pin.y * this.currentZoomLevel + this.imagePosition.y - 30) + 'px'
            };
        },

        updateComment() {
            const pin = this.pins.find(p => p.id === this.selectedPinId);
            if (pin) {
                pin.comment = this.currentComment;
            }
        },

        blurInput() {
            if (this.$refs.commentInput) {
                this.$refs.commentInput.blur();
            }
        },

        deletePin() {
            this.pins = this.pins.filter(p => p.id !== this.selectedPinId);
            this.selectedPinId = null;
            this.currentComment = '';
        },

        zoomIn() {
            this.zoomAroundCenter(1.2);
        },

        zoomOut() {
            this.zoomAroundCenter(1 / 1.2);
        },

        zoomAroundCenter(factor) {
            const container = this.$refs.mapContainer;
            if (!container) return;

            const oldZoom = this.currentZoomLevel;
            const newZoom = Math.min(Math.max(oldZoom * factor, 0.1), 3);

            if (newZoom === oldZoom) return; // Keine Änderung

            this.currentZoomLevel = newZoom;

            // Mittelpunkt des sichtbaren Bereichs berechnen
            const containerRect = container.getBoundingClientRect();
            const centerX = containerRect.width / 2;
            const centerY = containerRect.height / 2;

            // Punkt im Bild, der im Zentrum des Viewports ist
            const imagePointX = (centerX - this.imagePosition.x) / oldZoom;
            const imagePointY = (centerY - this.imagePosition.y) / oldZoom;

            // Neue Position berechnen, damit der Punkt in der Mitte bleibt
            this.imagePosition = {
                x: centerX - imagePointX * this.currentZoomLevel,
                y: centerY - imagePointY * this.currentZoomLevel
            };
        }
    },

    beforeDestroy() {
        if (this.emitTimeout) {
            clearTimeout(this.emitTimeout);
        }
    }
};
</script>

<style scoped>
.pin-limit-warning {
    color: #b53c00;
    background: #fffbe7;
    border: 1px solid #ffd694;
    border-radius: 4px;
    padding: 8px 12px;
    margin: 12px 0 0 0;
    text-align: center;
    font-size: 0.95em;
}

.floor-plan-component {
    width: 100%;
}

.map-container {
    position: relative;
    width: 100%;
    border: 2px solid #ccc;
    overflow: hidden;
    cursor: grab;
    background-color: #f5f5f5;
    touch-action: none; /* Verhindert Standard-Touch-Verhalten */
}

.map-container:active {
    cursor: grabbing;
}

.zoom-controls {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 20;
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.zoom-btn {
    width: 40px;
    height: 40px;
    border: 2px solid #333;
    background-color: white;
    color: #333;
    font-size: 20px;
    font-weight: bold;
    cursor: pointer;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
}

.zoom-btn:hover {
    background-color: #f0f0f0;
    transform: scale(1.05);
}

.zoom-btn:active {
    transform: scale(0.95);
}

.floor-plan-image {
    position: absolute;
    max-width: none;
    max-height: none;
    user-select: none;
    pointer-events: none;
}

.pin {
    position: absolute;
    cursor: pointer;
    z-index: 10;
    transition: transform 0.2s ease;
    -webkit-tap-highlight-color: transparent;
}

.pin:hover {
    transform: scale(1.1);
}

.pin-marker {
    width: 30px;
    height: 30px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    border: 3px solid white;
    box-shadow: 0 4px 8px rgba(0,0,0,0.5);
}

.pin:not(.pin-active) .pin-marker {
    background-color: #dc3545;
}

.pin-active .pin-marker {
    background-color: #28a745;
}

.comment-section {
    margin-top: 15px;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 5px;
    background-color: #f8f9fa;
}

/* Mobile Optimierungen */
@media (max-width: 768px) {
    .zoom-controls {
        top: 5px;
        left: 5px;
    }

    .zoom-btn {
        width: 35px;
        height: 35px;
        font-size: 18px;
    }

    .pin-marker {
        width: 35px;
        height: 35px;
    }
}
</style>
